import { Component, computed, Inject, input, OnDestroy, OnInit, Optional, output, signal, ViewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SettingsService } from '@app/services/settings.service';
import { FileOpener } from '@capacitor-community/file-opener';
import { Capacitor } from '@capacitor/core';
import { BundleType, DOCUMENT_PREVIEW_CONFIG, EntityTypeId, FieldName, FileModel, FilePreviewType, MediaPresignApiService, ProviderType, SlideType } from 'library-explorer';
import { NgxDocViewerComponent } from 'ngx-doc-viewer';
import { of, Subscription } from 'rxjs';
import { delay, finalize, switchMap } from 'rxjs/operators';
import { UserActivityType } from 'src/app/model/enums/user-activity-type.enum';
import { DownloadService } from 'src/app/services/api/download.service';
import { UserActivityService } from 'src/app/services/user-activity.service';
import { PdfViewerComponent } from './components/pdf-viewer/pdf-viewer.component';
import { ViewerTypeExtensions } from './document-viewer-extensions';


@Component({
    selector: 'app-document-preview',
    templateUrl: './document-preview.component.html',
    styleUrls: ['./document-preview.component.scss'],
    standalone: false
})
export class DocumentPreviewComponent implements OnInit, OnDestroy {
  @ViewChild(NgxDocViewerComponent, { static: false }) public viewer?: NgxDocViewerComponent;
  @ViewChild(PdfViewerComponent, { static: false }) public pdfViewer?: PdfViewerComponent;
  
  public uri = input<string>();
  public fileName = input<string>();
  public document = input<FileModel>();
  
  public entityId = input<string | null>();
  public bundle = input<BundleType | null>();
  public entityTypeId = input<EntityTypeId | null>();
  public fileKey = input<string | null>();
  public provider = input<ProviderType>(ProviderType.AWS);
  public insideDialog = input<boolean>(false);
  public forceFullScreen = input<boolean>(false);
  public disableDownload = input<boolean>(false);
  public isLoaded = output<boolean>();


  public isLoading = signal(true);
  public isDownloadLoading = signal(false);
  public watermarkEnabled = signal(false);
  public documentDownloadEnabled = signal(false);

  public allowDownloadFile = computed(() => {
    return !this.disableDownload() && this.documentDownloadEnabled();
  });

  public showFullScreenBtn = computed(() => {
    return this.mode() !== FilePreviewType.VIDEO_VIEWER;
  });

  public isFileSupportedByViewer = computed(() => {
    const fileSrc = this.fileSrc();
    const mode = this.mode();

    return fileSrc ? !!mode : true;
  });

  public mode = computed(() => {
    const fileExtension = this.fileExtension();

    if (!fileExtension) {
      return null;
    }
  
    const modes = this.config?.supportedTypes ?? Object.values(FilePreviewType);
    return modes.find(key => ViewerTypeExtensions[key].indexOf(fileExtension) !== -1) as FilePreviewType;
  });

  public resolvedFileName = computed(() =>
    this.fileName() ?? this.document()?.name ?? this.document()?.filename ?? this.getFilenameFromUri(this.uri()) ?? ''
  );

  public fileExtension = computed(() => {
    const filename = this.resolvedFileName();

    if (!filename) {
      return null;
    }

    return filename?.split('.').reverse()[0].toLocaleLowerCase();
  });

  public fileSrc = toSignal(
    toObservable(
      computed(() => ({
        uri: this.uri(),
        document: this.document(),
        entityTypeId: this.entityTypeId(),
        bundle: this.bundle(),
      }))
    ).pipe(
      switchMap(({ uri, document, entityTypeId, bundle }) => {
        if (uri) {
          return of(uri);
        }

        if (!document) {
          return of(null);
        }

        if (document.isOffline) {
          return of(document.uri);
        }

        return this.mediaPresignApiService.getPreSignedUrl(
          entityTypeId || EntityTypeId.PARAGRAPH,
          bundle || SlideType.SlideEmbeddedDocument as any,
          FieldName.AWS_FILE,
          document
        );
      })
    ),
    { initialValue: null }
  );

  public readonly modes: typeof FilePreviewType = FilePreviewType;
  public readonly activityTypes: typeof UserActivityType = UserActivityType;

  private reloadGoogleInterval?: Subscription;

  constructor(
    @Inject(DOCUMENT_PREVIEW_CONFIG) @Optional() public config: { supportedTypes: FilePreviewType[] },
    private readonly userActivityService: UserActivityService,
    private readonly downloadService: DownloadService,
    private readonly settingsService: SettingsService,
    private readonly mediaPresignApiService: MediaPresignApiService
  ) {
  }

  public loaded(): void {
    if (this.reloadGoogleInterval) {
      if (this.viewer?.iframes && this.viewer?.iframes.first && this.viewer?.iframes.first.nativeElement) {
        this.isLoaded.emit(true);
        this.isLoading.set(false);
        this.reloadGoogleInterval.unsubscribe();
      }

      return;
    }

    this.isLoading.set(false);
    this.isLoaded.emit(true);
  }

  public startLoading(): void {
    this.isLoading.set(true);
  }

  ngOnInit() {
    this.settingsService.getSettings()
      .subscribe(data => {
        this.documentDownloadEnabled.set(data.lesson.libraryDocumentDownload !== false);
        this.watermarkEnabled.set(data.security?.watermark?.enableWatermarkForPdf ?? false);
      });
  }

  ngOnDestroy(): void {
    if (this.reloadGoogleInterval) {
      this.reloadGoogleInterval.unsubscribe();
    }
  }

  public logUserVideoActivity(type: UserActivityType): void {
    const entityId = this.entityId();
    const key = this.fileKey();

    if (!entityId || !key) {
      return;
    }

    this.userActivityService.logUserVideoActivity(type, entityId, EntityTypeId.NODE, { key });
  }

  public downloadFile(): void {
    const src = this.fileSrc();
    const fileName = this.resolvedFileName();

    if (!src) {
      return;
    }

    // Opens document if user is on Native mobile device
    if (this.document()?.isOffline && Capacitor.isNativePlatform()) {
      this.openFile();
    }

    if (this.isDownloadLoading()) {
      return;
    }

    this.isDownloadLoading.set(true);
  
    this.downloadService.downloadFile(src, this.document()?.provider ?? this.provider(), fileName)
      .pipe(
        delay(200),
        finalize(() => this.isDownloadLoading.set(false)),
      )
      .subscribe(() => {
        const entityId = this.entityId();

        if (entityId) {
          this.userActivityService.logUserDownloadFileActivity(entityId);
        }
      });
  }

  private getFilenameFromUri(uri: string | null | undefined): string | null {
    if (!uri) {
      return null;
    }

    try {
      return new URL(uri).pathname.split('/').pop() ?? null;
    } catch {
      return null;
    }
  }

  async openFile() {
    const filePath = this.document()?.filePath;

    if (!filePath) {
      return;
    }
  
    await FileOpener.open({ filePath });
  }
}
